import { atom } from "jotai";
import { atomFamily } from "jotai-family";
import { atomWithMutation, atomWithQuery, queryClientAtom } from "jotai-tanstack-query";
import { ApiError } from "@/api/client";
import { addNote, saveModel, saveModelTemplate } from "@/modules/plus/data/plus.data.api";
import {
  accessoryPhotosBaselineQueryOptions,
  dataKeys,
  modelsQueryOptions,
  modelTemplatesQueryOptions,
  notesQueryOptions,
} from "@/modules/plus/data/plus.data.query";
import type { Model, ModelTemplate } from "@/modules/plus/plus.schema";
import type { AtomContainer } from "@/state";

// Canonical data — all from the PLUS server. There's no static fallback:
// the kiosk shows a "loading" overlay during the cold-start window and the
// in-memory cache (TanStack Query) keeps subsequent loads instant.
export class DataAtoms {
  constructor(private readonly atoms: AtomContainer) {}

  // --- bulk reference data -----------------------------------------------

  // Gated on auth — the API requires a signed-in user, so firing these before
  // the Firebase session restores yields 401s and the UI surfaces a misleading
  // "wrong account" message. With `enabled` tied to `isSignedIn`, the queries
  // don't fire until auth is settled, and resume automatically on sign-in.
  public modelsQuery = atomWithQuery((get) => ({
    ...modelsQueryOptions(),
    enabled: get(this.atoms.auth.isSignedIn),
  }));
  public templatesQuery = atomWithQuery((get) => ({
    ...modelTemplatesQueryOptions(),
    enabled: get(this.atoms.auth.isSignedIn),
  }));
  public accessoryBaselineQuery = atomWithQuery((get) => ({
    ...accessoryPhotosBaselineQueryOptions(),
    enabled: get(this.atoms.auth.isSignedIn),
  }));

  // Convenience accessors. Always return a value (empty record while loading
  // — callers branch on `isLoading` for explicit loading states).
  public models = atom<Record<string, Model>>((get) => get(this.modelsQuery).data ?? {});

  public templates = atom<readonly ModelTemplate[]>((get) =>
    Object.values(get(this.templatesQuery).data ?? {}),
  );

  public accessoryBaseline = atom<Record<string, string>>(
    (get) => get(this.accessoryBaselineQuery).data ?? {},
  );

  // True until all three bulk queries have resolved — used by the layout
  // loading overlay so the kiosk doesn't render an empty list mid-fetch.
  public isLoading = atom<boolean>((get) => {
    const m = get(this.modelsQuery);
    const t = get(this.templatesQuery);
    const b = get(this.accessoryBaselineQuery);
    return m.isLoading || t.isLoading || b.isLoading;
  });

  // True if any bulk query has failed (after TanStack retries). The overlay
  // surfaces this instead of leaving the page silently empty when the server
  // rejects reads (e.g., wrong Microsoft account signed in).
  public errorMessages = atom<string[]>((get) => {
    const errs: string[] = [];
    for (const [label, q] of [
      ["models", get(this.modelsQuery)],
      ["model_templates", get(this.templatesQuery)],
      ["accessory_photos_baseline", get(this.accessoryBaselineQuery)],
    ] as const) {
      if (q.isError && q.error) {
        const message = q.error instanceof Error ? q.error.message : String(q.error);
        errs.push(`${label}: ${message}`);
      }
    }
    return errs;
  });

  public hasError = atom<boolean>((get) => get(this.errorMessages).length > 0);

  // The server enforces the allowlist: a signed-in-but-not-allowed account
  // gets 403s. The overlay uses this to show the "account not allowed" copy
  // instead of a generic failure.
  public isForbidden = atom<boolean>((get) =>
    [get(this.modelsQuery), get(this.templatesQuery), get(this.accessoryBaselineQuery)].some(
      (q) => q.error instanceof ApiError && q.error.status === 403,
    ),
  );

  // --- per-model notes ---------------------------------------------------

  public notesByCode = atomFamily((modelCode: string) =>
    atomWithQuery(() => notesQueryOptions(modelCode)),
  );

  // --- mutations ---------------------------------------------------------
  // The server stamps the acting user from the session — no updatedBy/author
  // parameters here any more.

  public saveModel = atomWithMutation((get) => ({
    mutationFn: async (model: Model) => {
      await saveModel({ model });
    },
    onSuccess: () => {
      get(queryClientAtom).invalidateQueries({ queryKey: dataKeys.models });
    },
  }));

  public saveTemplate = atomWithMutation((get) => ({
    mutationFn: async (template: ModelTemplate) => {
      await saveModelTemplate({ template });
    },
    onSuccess: () => {
      get(queryClientAtom).invalidateQueries({ queryKey: dataKeys.templates });
    },
  }));

  public addNote = atomWithMutation((get) => ({
    mutationFn: async (args: { modelCode: string; text: string }) => {
      await addNote({ modelCode: args.modelCode, text: args.text });
    },
    onSuccess: (_data, vars) => {
      get(queryClientAtom).invalidateQueries({
        queryKey: dataKeys.notes(vars.modelCode),
      });
    },
  }));
}
